/**
 * Finding the element a step points at, and keeping a box drawn around it while the page moves.
 *
 * Two problems that a naive `document.querySelector` in a useEffect gets wrong:
 *
 * 1. THE ELEMENT IS NOT THERE YET. Every module in this app is React.lazy + Suspense (App.jsx:20-31),
 *    so after navigate('/timekeeping/reports') the chunk is still downloading and the route renders
 *    a spinner. A single query one tick later finds nothing and would declare a perfectly healthy
 *    anchor missing. So: query, then WATCH with a MutationObserver until it appears or a deadline
 *    passes. "Missing" is a conclusion with a timeout behind it, not a first impression.
 *
 * 2. THE PAGE MOVES. Scroll, resize, a collapsing section, the sticky header — the highlight has to
 *    follow. Note the scroll listener is registered with capture:true: scroll events do not bubble,
 *    and half the anchored elements in this app live inside their own scroll container.
 */
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { findAnchor } from './tourAnchors'

export const ANCHOR_STATUS = Object.freeze({
  IDLE: 'idle',
  RESOLVING: 'resolving',
  FOUND: 'found',
  MISSING: 'missing',
  /** Two elements answer to one anchor — the overlay cannot know which one the author meant. */
  AMBIGUOUS: 'ambiguous',
})

/** jsdom (and any non-visual host) may not provide rAF; a macrotask is a fine substitute here. */
const schedule = (fn) => (
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 0)
)
const unschedule = (handle) => {
  if (handle == null) return
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle)
  else clearTimeout(handle)
}

/**
 * @param {string|null} anchorName
 * @param {{ enabled?: boolean, timeoutMs?: number }} options
 * @returns {{ status: string, element: Element|null, count: number }}
 */
export function useAnchorElement(anchorName, { enabled = true, timeoutMs = 4000 } = {}) {
  const [state, setState] = useState({ status: ANCHOR_STATUS.IDLE, element: null, count: 0, anchor: null })

  useEffect(() => {
    if (!enabled || !anchorName) {
      setState({ status: ANCHOR_STATUS.IDLE, element: null, count: 0, anchor: null })
      return undefined
    }

    let settled = false
    let observer = null
    let deadline = null
    let pending = null

    const attempt = () => {
      const { element, count } = findAnchor(anchorName)
      if (count === 0) return false
      settled = true
      setState({
        // Ambiguity is reported, never silently resolved to the first match. Picking one at random
        // is how a tour ends up confidently highlighting the wrong button.
        status: count > 1 ? ANCHOR_STATUS.AMBIGUOUS : ANCHOR_STATUS.FOUND,
        element,
        count,
        anchor: anchorName,
      })
      return true
    }

    setState({ status: ANCHOR_STATUS.RESOLVING, element: null, count: 0, anchor: anchorName })

    if (!attempt()) {
      // Coalesce: a React render produces a burst of mutations and querying on each is pure waste.
      const onMutation = () => {
        if (settled || pending != null) return
        pending = schedule(() => { pending = null; if (!settled) attempt() })
      }
      observer = new MutationObserver(onMutation)
      observer.observe(document.documentElement, { childList: true, subtree: true })
      deadline = setTimeout(() => {
        if (settled) return
        settled = true
        setState({ status: ANCHOR_STATUS.MISSING, element: null, count: 0, anchor: anchorName })
      }, timeoutMs)
    }

    return () => {
      settled = true
      if (observer) observer.disconnect()
      if (deadline) clearTimeout(deadline)
      unschedule(pending)
    }
  }, [anchorName, enabled, timeoutMs])

  // DERIVED DURING RENDER, not synced in an effect. State updates land a render LATER than the prop
  // that caused them, so for exactly one render after the step advances, `state` still describes the
  // PREVIOUS step's anchor. That one render was a real bug: a step whose anchor was missing set
  // status=MISSING, the overlay skipped forward, and on the very next render the new step was paired
  // with the old MISSING status — so the skip fired a second time and ran the tour off the end.
  // Answering RESOLVING until the effect has actually looked for THIS anchor makes stale status
  // unrepresentable rather than merely unlikely.
  if (state.anchor !== anchorName) {
    return {
      status: enabled && anchorName ? ANCHOR_STATUS.RESOLVING : ANCHOR_STATUS.IDLE,
      element: null,
      count: 0,
      anchor: anchorName,
    }
  }
  return state
}

const sameRect = (a, b) => (
  a && b &&
  a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height
)

/**
 * Live viewport rect for an element.
 *
 * `measurable` is the load-bearing field. A 0×0 rect is not a bug to crash on — it is a real and
 * common state: the element is display:none behind a mobile breakpoint, inside a collapsed section,
 * or being rendered in jsdom, which gives every element a zero rect. Callers use it to fall back to
 * a centred card with no spotlight, because a 0×0 "spotlight" in the top-left corner reads as a
 * broken overlay.
 */
export function useElementRect(element) {
  const [rect, setRect] = useState(null)
  const rectRef = useRef(null)

  const measure = useCallback(() => {
    if (!element) { rectRef.current = null; setRect(null); return }
    const r = element.getBoundingClientRect()
    const next = {
      top: r.top, left: r.left, width: r.width, height: r.height,
      bottom: r.top + r.height, right: r.left + r.width,
      measurable: r.width > 0 && r.height > 0,
    }
    if (sameRect(rectRef.current, next)) return
    rectRef.current = next
    setRect(next)
  }, [element])

  useLayoutEffect(() => {
    measure()
    if (!element) return undefined

    let pending = null
    const onChange = () => {
      if (pending != null) return
      pending = schedule(() => { pending = null; measure() })
    }

    // capture:true — scroll does not bubble, so a listener on window would never hear a scroll
    // inside a nested overflow container, and the highlight would drift away from its element.
    window.addEventListener('scroll', onChange, true)
    window.addEventListener('resize', onChange)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onChange) : null
    if (ro) ro.observe(element)

    return () => {
      window.removeEventListener('scroll', onChange, true)
      window.removeEventListener('resize', onChange)
      if (ro) ro.disconnect()
      unschedule(pending)
    }
  }, [element, measure])

  return rect
}
